import { Component, Inject, OnInit, Optional, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormControl } from '@angular/forms';
import { AbsenceScheduleType, Colors, ConfirmationResponse, InputType } from '@enums';
import { UserService } from '@appServices/user.service';
import moment from 'moment';
import { AbsenceScheduleRequest, AbsenceScheduleResponse, UserSummary } from '@yellowtail-frontend/cord-connector';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { HelperService } from '@utilServices/helper.service';
import { VariableService } from '@utilServices/variables.service';
import { FormatService } from '@utilServices/format.service';

@Component({
    selector: 'app-profile-out-of-office-modal',
    templateUrl: './profile-out-of-office-modal.component.html',
    styleUrls: ['./profile-out-of-office-modal.component.scss'],
    standalone: false
})
export class ProfileOutOfOfficeModalComponent implements OnInit, OnDestroy {

    // Enums
    inputType = InputType;
    colors = Colors;
    rows = 4;

    // Select options
    assigneeUsers: UserSummary[] = [];
    monthDays: string[] = [];

    radioOptionsType = [
        { name: AbsenceScheduleType.Eenmalig, checked: true, displayOption: 'Eenmalig' },
        { name: AbsenceScheduleType.Wekelijks, checked: false, displayOption: 'Wekelijks' },
        { name: AbsenceScheduleType.Maandelijks, checked: false, displayOption: 'Maandelijks' }
    ];
    weekNames = [
        {
            name: 'Maandag', value: 'Monday'
        },
        {
            name: 'Dinsdag', value: 'Tuesday'
        },
        {
            name: 'Woensdag', value: 'Wednesday'
        },
        {
            name: 'Donderdag', value: 'Thursday'
        },
        {
            name: 'Vrijdag', value: 'Friday'
        }
    ];
    monthWeekIntervals = [
        {
            name: '1e', value: 1
        },
        {
            name: '2e', value: 2
        },
        {
            name: '3e', value: 3
        },
        {
            name: '4e', value: 4
        }
    ];
    repeatOptions_week = [
        { name: 'Elke week', value: 1 },
        { name: 'Eens in de 2 weken', value: 2 },
        { name: 'Eens in de 3 weken', value: 3 }
    ];
    repeatOptions_month = [
        { name: 'Elke maand', value: 1 },
        { name: 'Eens in de 2 maanden', value: 2 },
        { name: 'Eens in de 3 maanden', value: 3 },
        { name: 'Eens in de 4 maanden', value: 4 },
        { name: 'Eens in de 5 maanden', value: 5 },
        { name: 'Eens in de 6 maanden', value: 6 }
    ];

    // Checkboxes for weekly settings
    weekArray = [
        { name: 'Monday', checked: false, displayName: 'Ma' },
        { name: 'Tuesday', checked: false, displayName: 'Di' },
        { name: 'Wednesday', checked: false, displayName: 'Wo' },
        { name: 'Thursday', checked: false, displayName: 'Do' },
        { name: 'Friday', checked: false, displayName: 'Vr' },
    ];

    // Radio
    checkedRadio_type = { name: AbsenceScheduleType.Eenmalig, checked: true };

    // Form
    newNotification: UntypedFormGroup;
    dateToCheckbox: FormControl<boolean> = new FormControl<boolean>(false);
    timeCheckbox: FormControl<boolean> = new FormControl<boolean>(false);

    startDateTimeDefault = moment.utc().set({'seconds': 0, 'minute': 0, 'hour': 8});
    endDateTimeDefault = moment.utc().set({'seconds': 0, 'minute': 0, 'hour': 18});

    // States
    notification = {
        id: '',
        assignee: {
            firstName: '',
            lastName: '',
        },
        Type: '',
        fromDate: '',
        toDate: '',
    };

    constructor(
        public dialog: MatDialogRef<ProfileOutOfOfficeModalComponent>,
        private readonly _dialog: MatDialog,
        private readonly fb: UntypedFormBuilder,
        private readonly _userService: UserService,
        private readonly _helperService: HelperService,
        private readonly _variableService: VariableService,
        private readonly _formatService: FormatService,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this._variableService.displayTimeForDatePicker = false;
     }

    absenceScheduleId: string;
    monthRepeat: UntypedFormControl = new UntypedFormControl('single');

    ngOnInit(): void {
        this.monthRepeat.valueChanges.subscribe(value => {
            switch (value) {
                case 'single':
                    this.monthRepeatDay.enable();
                    this.monthRepeatWeekInterval.disable();
                    this.monthRepeatWeekDay.disable();
                    break;

                case 'repeat':
                    this.monthRepeatDay.disable();
                    this.monthRepeatWeekInterval.enable();
                    this.monthRepeatWeekDay.enable();
                    break;

                default:
                    break;
            }
        });

        this.checkDayOfWeekCheckboxes();
        this.getMonthDays();
        this.getUserList();
        this.checkType({ name: this.data?.Type ? this.data?.Type : AbsenceScheduleType.Eenmalig, checked: true });

        this.absenceScheduleId = this.data?.ID ? this.data?.ID : undefined;

        this.dateToCheckbox = new UntypedFormControl(this.data ? (this.data?.EndDate ? true : false) : false, []);

        this.newNotification = this.fb.group({
            frequency: this.data?.Frequency ? this.checkScheduleType(this.data?.Frequency) : '',
            monthRepeatDay: this.data?.DayOfMonth ? this.data?.DayOfMonth : '',
            monthRepeatWeekInterval: this.data?.Day ? this.data?.Day : '',
            monthRepeatWeekDay: this.data?.DayOfWeek?.length === 1 ? this.data?.DayOfWeek[0] : '',
            assignee: {},
            automaticReply: this.data?.AutomaticReply ? this.data?.AutomaticReply : '',
            dateFrom: this.data?.StartDate ? moment(this.data?.StartDate).format(this._formatService.dateFormatDB) : '',
            dateTo: this.getEndDate(this.data?.EndDate, false),
            timeFrom: this.data?.StartDate ? moment(this.data?.StartDate).format('HH:mm') : '12:00',
            timeTo: this.getEndDate(this.data?.EndDate, true),
        });

        this.monthRepeatWeekDay.disable();
        this.monthRepeatWeekInterval.disable();

        if (this.data) {
            if (this.monthRepeatWeekInterval.value !== '' && this.monthRepeatWeekDay.value !== '') {
                this.monthRepeat.setValue('repeat');
                this.monthRepeatWeekInterval.setValue(this.monthWeekIntervals.find(interval => interval.value === this.data.Day));
                this.monthRepeatWeekDay.setValue(this.weekNames.find(dayOfWeek => dayOfWeek.value === this.data.DayOfWeek[0]));
            } else {
                this.monthRepeat.setValue('single');
            }
        }

        if (this.dateFrom.value) {
            if (!this.isStartOfDay(moment(`${(moment(this.dateFrom.value).format(this._formatService.dateFormatDB))}T${this.timeFrom.value}`).utc().local())) {
                this.timeCheckbox.setValue(true);
            }
        }
    }

    ngOnDestroy(): void {
        this._variableService.displayTimeForDatePicker = false;
    }

    checkScheduleType(frequency): number | { name: string, value: number } {
        switch (this.checkedRadio_type.name) {
            case AbsenceScheduleType.Eenmalig:
                return frequency;
            case AbsenceScheduleType.Wekelijks:
                return this.repeatOptions_week.find(option => option.value === frequency);
            case AbsenceScheduleType.Maandelijks:
                return this.repeatOptions_month.find(option => option.value === frequency);
        }
    }

    checkDayOfWeekCheckboxes(): void {
        if (this.data?.DayOfWeek) {
            this.data?.DayOfWeek.forEach(day => {
                this.weekArray.forEach(x => {
                    if (day === x.name) {
                        x.checked = true;
                    }
                });
            });
        }
    }

    getMonthDays(): void {

        const emptyArray = [];
        const totalDays = moment().daysInMonth();
        for (let i = 0; i < totalDays; i++) {
            emptyArray.push(i + 1);
        }

        this.monthDays = emptyArray;
    }

    getUserList(): void {
        this._userService.getUsersWithFullName()
            .then(response => {
                this.assigneeUsers = response;

                if (this.notification?.assignee?.firstName && this.notification?.assignee?.lastName) {
                    const assignedUser = this._helperService.getUserFullName(this.notification.assignee, this.assigneeUsers);
                    if (assignedUser) {
                        this.assignee.setValue(assignedUser);
                    }
                }

                if (this.absenceScheduleId) {
                    this.getDisplayName(this.data?.ForwardingEmail);
                }
            }).catch((err) => {
                this._helperService.displayGenericErrorAlert(err);
            });
    }

    getDisplayName(forwardingEmail): void {
        const TEST_USER = this.assigneeUsers.find(user => user?.profile?.email === forwardingEmail);
        if (TEST_USER) {
            this.assignee.setValue(TEST_USER);
        }
    }

    // UI

    disableAddButton(): boolean {

        if (!this.checkedRadio_type.checked) {
            return true;
        }
        if (!this.dateFrom.value) {
            return true;
        }

        switch (this.checkedRadio_type.name) {
            case AbsenceScheduleType.Maandelijks:
                return false;
            case AbsenceScheduleType.Eenmalig:
                return false;
            case AbsenceScheduleType.Wekelijks:
                if (!this.frequency.value) {
                    return true;
                }

                this.weekArray.forEach(day => {
                    if (!day.checked) {
                        return true;
                    }
                });
        }
    }

    disableUpdateButton(): boolean {

        if (this.dateFrom.valid && (this.dateTo.disabled || this.dateTo.valid)) {
            return false;
        }
        
        return true;
    }

    addAbsenceSchedule(): void {

        // FREQUENCY
        // --------------------------------------------------------
        let FREQUENCY = 0;

        // Eenmaalig
        if (this.radioOptionsType[0].checked) {
            FREQUENCY = 1;
        }
        // Wekelijks || Maandelijks
        if (this.radioOptionsType[1].checked || this.radioOptionsType[2].checked) {
            FREQUENCY = this.frequency.value.value;
        }

        // DAY OF MONTH
        // --------------------------------------------------------
        const DAY_OF_MONTH = this.monthRepeatDay.value || 0;

        // DAY
        // --------------------------------------------------------
        const DAY = this.monthRepeatWeekInterval.value.value;

        // DAY OF WEEK
        // --------------------------------------------------------
        const DAY_OF_WEEK = [];

        // Wekelijks
        if (this.radioOptionsType[1].checked) {
            this.weekArray.forEach(day => {
                if (day.checked) {
                    DAY_OF_WEEK.push(day.name);
                }
            });
        }

        // Maandelijks
        if (this.radioOptionsType[2].checked) {
            if (this.monthRepeatWeekInterval.value) {
                DAY_OF_WEEK.push(this.monthRepeatWeekDay.value.value);
            }
        }

        const NEW_ABSENCE: AbsenceScheduleRequest = {
            Type: this.checkedRadio_type.name,
            Frequency: FREQUENCY,
            DayOfMonth: DAY_OF_MONTH, // Only applies to Absence schedules of type Monthly
            Day: DAY, // Only applies to Absence schedules of type Monthly and needs to be used in conjunction with DayOfWeek
            DayOfWeek: DAY_OF_WEEK,
            StartDate: moment(`${(moment(this.dateFrom.value).format(this._formatService.dateFormatDB))}T${this.timeFrom.value}`).utc().format(),
            EndDate: this.setEndDate(this.dateTo.value),
            ForwardingEmail: this.assignee?.value?.profile?.email,
            AutomaticReply: this.automaticReply.value,
        };

        if (!this.timeCheckbox.value) {
            NEW_ABSENCE.StartDate = moment(NEW_ABSENCE.StartDate).startOf('day').utc().format();
            if (NEW_ABSENCE.EndDate !== '') {
                NEW_ABSENCE.EndDate = moment(NEW_ABSENCE.EndDate).endOf('day').utc().format();
            }
        }

        // Only applies to Absence schedules of type Monthly:
        // DayOfMonth
        // Day
        if (this.checkedRadio_type.name !== AbsenceScheduleType.Maandelijks) {
            delete NEW_ABSENCE.DayOfMonth;
            delete NEW_ABSENCE.Day;
        }

        // Only applies to Absence schedules of type Monthly && Weekly:
        // DayOfWeek
        if (this.checkedRadio_type.name === AbsenceScheduleType.Eenmalig) {
            delete NEW_ABSENCE.DayOfWeek;
        }

        this._userService.saveAbsenceSchedule(NEW_ABSENCE, this.data?.userID || undefined)
            .then((newAbsenceId) => {

                const ABSENCE_RESPONSE: AbsenceScheduleResponse = {
                    ID: newAbsenceId,
                    ...NEW_ABSENCE
                };

                this.dialog.close(ABSENCE_RESPONSE);

                this._helperService.displayGenericSuccessAlert('Afwezigheid toegevoegd');

            })
            .catch(err => {
                const EXISTING_SCHEDULE: boolean
                    = (err.response?.status === 400 && err.result?.includes('existing Absence schedule'));

                this._helperService.displayGenericErrorAlert(
                    err,
                    EXISTING_SCHEDULE ? 'Er is al een afwezigheidsmelding met dezelfde data' : 'Oeps! Er is iets misgegaan'
                );
            });
    }

    updateAbsenceSchedule(): void {

        const ABSENCE_REQUEST: AbsenceScheduleRequest = {
            Id: this.absenceScheduleId,
            Type: this.checkedRadio_type.name,
            StartDate: moment(`${(moment(this.dateFrom.value).format(this._formatService.dateFormatDB))}T${this.timeFrom.value}`).utc().format(),
            EndDate: this.setEndDate(this.dateTo.value),
            AutomaticReply: this.automaticReply.value,
            ForwardingEmail: this.assignee.value?.profile?.email,
            Frequency: this.frequency.value.value,
            DayOfWeek: [],
            Day: this.monthRepeatWeekInterval.value?.value,
            DayOfMonth: this.monthRepeatDay.value
        };

        if (!this.timeCheckbox.value) {
            ABSENCE_REQUEST.StartDate = moment(ABSENCE_REQUEST.StartDate).startOf('day').utc().format();
            if (ABSENCE_REQUEST.EndDate !== '') {
                ABSENCE_REQUEST.EndDate = moment(ABSENCE_REQUEST.EndDate).endOf('day').utc().format();
            }
        }

        if (this.checkedRadio_type.name === AbsenceScheduleType.Wekelijks) {
            this.weekArray.forEach(day => {
                if (day.checked) {
                    ABSENCE_REQUEST.DayOfWeek.push(day.name);
                }
            });
        } else if (this.checkedRadio_type.name === AbsenceScheduleType.Maandelijks) {
            if (this.monthRepeatWeekDay.value?.value) {
                ABSENCE_REQUEST.DayOfWeek.push(this.monthRepeatWeekDay.value?.value);
            }
        }

        this._userService.updateAbsenceSchedule(this.absenceScheduleId, ABSENCE_REQUEST)
            .then((response: AbsenceScheduleResponse) => {
                this._helperService.displayGenericSuccessAlert('Afwezigheid gewijzigd');

                response.EndDate = this.dateToCheckbox.value ? response.EndDate : '';

                this.dialog.close(response);
            })
            .catch(error => {
                this._helperService.displayGenericErrorAlert(error);
                this.dialog.close({});
            });
    }

    cancel(): void {
        this.dialog.close();
    }

    deleteAbsenceSchedule(): void {
        this._dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Let op! Je verwijdert een afwezigheidsmelding',
                subtitle: 'Door op verwijder te klikken, verwijder je de afwezigheidsmelding',
                confirmButton: 'Verwijder',
                rejectButton: 'Annuleer'
            },
            disableClose: true
        })
        .afterClosed().subscribe((result: number) => {
            switch (result) {
                case ConfirmationResponse.yes:
                    this._userService.deleteAbsenceSchedule(this.absenceScheduleId)
                        .then(() => {

                            this._helperService.displayGenericSuccessAlert('Afwezigheid verwijderd');
                            this.dialog.close('delete');
                        })
                        .catch(error => {
                            this._helperService.displayGenericErrorAlert(error);
                            this.dialog.close();
                        });
                    break;
                case ConfirmationResponse.no:
                    break;
                case ConfirmationResponse.cancel:
                    break;
            }
        });
    }

    checkType(option): void {
        this.checkedRadio_type = option;

        const optionIndex = this.radioOptionsType.findIndex(radio => {
            return radio.name === option.name;
        });

        this.radioOptionsType[optionIndex].checked = true;
    }

    private setEndDate(date): string {
        if (!date || !this.dateToCheckbox.value) {
            return '';
        }
        return moment(`${(moment(date).format(this._formatService.dateFormatDB))}T${this.timeTo.value}`).utc().format();
    }

    private getEndDate(date, timeOnly: boolean): string {
        if (!date || !this.dateToCheckbox.value) {
            return timeOnly ? '12:00' : '';
        }

        if (timeOnly) {
           return moment(date).format('HH:mm');
        } else {
            return moment(date).format(this._formatService.dateFormatDB);
        }
    }

    private isStartOfDay(yourDateTime: moment.Moment): boolean {
        // Clone the moment object and set it to start of day
        const startOfDay = yourDateTime.clone().startOf('day');
        // Compare the original moment with the start of day
        return yourDateTime.isSame(startOfDay);
      }

    // Form

    get monthRepeatWeekDay(): AbstractControl {
        return this.newNotification.get('monthRepeatWeekDay');
    }

    get monthRepeatWeekInterval(): AbstractControl {
        return this.newNotification.get('monthRepeatWeekInterval');
    }

    // Days of month i.e. 1, 2, 3, 4, 5, 6 ... 22, 23, 24, ... 30, 31
    get monthRepeatDay(): AbstractControl {
        return this.newNotification.get('monthRepeatDay');
    }

    get frequency(): AbstractControl {
        return this.newNotification.get('frequency');
    }

    get assignee(): AbstractControl {
        return this.newNotification.get('assignee');
    }

    get automaticReply(): AbstractControl {
        return this.newNotification.get('automaticReply');
    }

    get dateFrom(): AbstractControl {
        return this.newNotification.get('dateFrom');
    }

    get dateTo(): AbstractControl {
        return this.newNotification.get('dateTo');
    }

    get timeFrom(): AbstractControl {
        return this.newNotification.get('timeFrom');
    }

    get timeTo(): AbstractControl {
        return this.newNotification.get('timeTo');
    }

    get displayTimeSelection(): boolean {
        return this.timeCheckbox.value;
    }
}
